import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Popup } from "semantic-ui-react";

import "components/Editable/Editable.scss";
import { useIsOverflowing } from "hooks";

type EditableProps = {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  hoverable?: boolean;
  valueClassName?: string;
  isNumberInput?: boolean;
  errorMessage?: string | null;
  onSubmit?: (t: string) => void;
  onValidation?: (value: string) => boolean;
};

export const Editable = ({ value, isNumberInput, disabled, onSubmit, onValidation, placeholder, hoverable, valueClassName = "", errorMessage = null }: EditableProps) => {
  const [text, setText] = useState(value);
  const [canEdit, setCanEdit] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const { ref: popupRef, isOverflowing } = useIsOverflowing<HTMLParagraphElement>();

  useEffect(() => {
    setText(value);
  }, [value]);

  const handleCanEdit = () => {
    if (!disabled) {
      setCanEdit(true);
    }
  };

  const handleFinishEditing = () => {
    setCanEdit(false);
    if (value !== text) {
      if (onValidation?.(text)) {
        onSubmit?.(text);
        return;
      } else if (onValidation && !onValidation(text)) {
        setText(value);
        toast.error(errorMessage);
        return;
      }
      onSubmit?.(text);
    }
  };

  useEffect(() => {
    if (canEdit && ref.current) {
      const len = ref.current.value.length;
      ref.current.setSelectionRange(len, len);
      ref.current.focus();
    }
  }, [canEdit, ref.current]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNumberInput && isNaN(Number(e.target.value))) return;

    setText(e.target.value);
  };

  return (
    <div className={`Editable ${disabled ? "disabled" : ""} ${text ? "" : "placeholder"}`}>
      {canEdit ? (
        <input type="text" ref={ref} value={text} onChange={handleChange} onBlur={handleFinishEditing} />
      ) : (
        <Popup
          disabled={!hoverable || !isOverflowing}
          hoverable
          content={text}
          trigger={
            <p ref={popupRef} onClick={handleCanEdit} className={valueClassName}>
              {text || placeholder}
            </p>
          }
        />
      )}
    </div>
  );
};
